import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  NewValueParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { permissionGrantedSignal } from '../../../../../../utils/asset-permissions.utils';
import { extractAutomaticMeasureFilter } from '../../../../../../utils/table-filter.utils';
import {
  FREQUENCY_OPTIONS,
  MeasureValueStatus,
  AutomaticMeasure,
  AutomaticMeasureValue,
} from '../../../../../../types/measure.interface';
import {
  MeasureStatusBadgeComponent,
  STATUS_LABEL_KEYS,
} from '../../../../../measures/measure-status-badge/measure-status-badge.component';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { ClipboardColumnType } from '../../../../../ui/grid-clipboard/grid-clipboard.utils';
import { DateNavigationService } from '../../../../../../services/date-navigation.service';
import { ViewPreferencesService } from '../../../../../../services/view-preferences.service';
import { AutomaticMeasureStore } from '../../../../../../stores/automatic-measure.store';
import { AutomaticMeasureValueStore, AutomaticValuesPage } from '../../../../../../stores/automatic-measure-value.store';
import { UnitStore } from '../../../../../../stores/unit.store';
import { FormulaInputsService } from '../../../../../../services/api/generated/formula-inputs/formula-inputs.service';
import { CesTimeSeriesDetailComponent } from '../../../../../ui/time-series-detail/ces-time-series-detail.component';
import { CesBottomActions } from '../../../../../ui/ces-bottom-actions/ces-bottom-actions.component';
import { NumericCellEditorComponent } from '../../../../../ui/grid-editors/numeric-cell-editor.component';
import { FormulaInputSummary } from '../../../../../../types/formula-input-entity.interface';
import { AutomaticValueDraftService, StagedValueUpdate } from './automatic-value-draft.service';
import { ToastService } from '../../../../../../services/toast.service';
import { measurementUpdateTargetsMeasure } from '../../../../../../utils/websocket-match';
import { registerMeasurementUpdateRefresh } from '../../../../../../utils/measurement-update-refresh';
import { registerComputingPoll } from '../../../../../../utils/computing-poll';
import { formatGridDateTime } from '../../../../../../utils/date.utils';
import { writeClipboardText } from '../../../../../../utils/clipboard.utils';
import { formatClipboardNumericValue, formatNumericValue } from '../../../../../../utils/grid/numeric-value-formatter';
import { ENTER_APPLY_NUMBER_FILTER_PARAMS, EXCEL_SET_FILTER_PARAMS } from '../../../../../../utils/grid/grid-presets';
import { CesDateRangeGridFilterComponent } from '../../../../../ui/grid-filters/date-range-grid-filter.component';
import {
  MeasureColumnToggleComponent,
  MeasureColumnVisibility,
} from '../../../../../measures/measure-column-toggle/measure-column-toggle.component';
import { AutomaticMeasureHeaderComponent, AutomaticMeasureHeaderParams } from './automatic-measure-header.component';

// AG Grid SSRM loads blocks on demand, so the in-memory buffer only holds what was
// scrolled. "Copy all" pages the whole window server-side instead; 1000 is the
// backend's max slice (_SSRM_PAGE_LIMIT), i.e. the largest request the API accepts.
const COPY_ALL_PAGE_SIZE = 1000;

interface DateSpan {
  from: Date;
  to: Date;
}

@Component({
  selector: 'app-measure-automatic-follow-up-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesBottomActions, CesTimeSeriesDetailComponent, MeasureColumnToggleComponent, TranslocoModule],
  providers: [DateNavigationService],
  templateUrl: './measure-automatic-follow-up-detail.component.html',
})
export class MeasureAutomaticFollowUpDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly automaticStore = inject(AutomaticMeasureStore);
  private readonly valueStore = inject(AutomaticMeasureValueStore);
  private readonly unitStore = inject(UnitStore);
  private readonly formulaInputsApi = inject(FormulaInputsService);
  private readonly translate = inject(TranslocoService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewPrefs = inject(ViewPreferencesService);
  private readonly draftSvc = inject(AutomaticValueDraftService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  // No onPending overlay: every frame — the enqueue 'pending' signal included —
  // triggers the same guarded silent refetch, and the refetched rows carry the
  // per-row truth (the server marks rows Pending while an active job covers
  // them). The computing poll below keeps refetching until the queue settles.
  private readonly wsRefresh = registerMeasurementUpdateRefresh({
    targets: payload => measurementUpdateTargetsMeasure(payload, this.currentMeasureId()),
    refresh: () => this.onRemoteMeasureUpdate(),
  });

  private readonly computingPoll = registerComputingPoll({
    refresh: () => this.refreshGridData(),
    // A tick landing mid-edit or mid-draft re-arms instead of churning the grid —
    // same guard as the websocket settles.
    isBlocked: () => (this.timeSeries()?.isEditing() ?? false) || this.hasDraft(),
  });

  readonly dateNav = inject(DateNavigationService);

  private readonly timeSeries = viewChild<CesTimeSeriesDetailComponent<AutomaticMeasureValue>>(CesTimeSeriesDetailComponent);

  private readonly measure = signal<AutomaticMeasure | null>(null);
  readonly isLoading = signal(false);
  private readonly rowsLoading = signal(false);
  readonly loadError = signal(false);
  private readonly unitSymbol = signal('');
  private readonly acquisitionUnitSymbol = signal('');
  // The acquired value is raw SCADA data: its unit lives on the formula's
  // acquisition parameter, not on the measure (which carries the calculated unit).
  private readonly acquiredValueUnit = computed(() => this.acquisitionUnitSymbol() || this.unitSymbol());
  private readonly loadedRows = signal<AutomaticMeasureValue[]>([]);
  private readonly totalRowCount = signal(0);
  // Last SSRM query the grid served (sort + filter): "copy all" replays it so the
  // clipboard matches the sorted/filtered window on screen, not the raw date range.
  private readonly lastQuery = signal<{
    sortField: string | undefined;
    sortOrder: 'asc' | 'desc';
    filter: ReturnType<typeof extractAutomaticMeasureFilter>;
  } | null>(null);
  readonly measureReady = computed(() => this.measure() !== null);
  private readonly hasRefreshed = signal(false);
  private readonly lastFetchSettled = signal(false);
  readonly gridReady = computed(() => this.measureReady() && this.hasRefreshed());
  readonly isEmpty = computed(() => this.lastFetchSettled() && !this.rowsLoading() && this.totalRowCount() === 0);

  readonly visibleColumns = signal<MeasureColumnVisibility>(
    this.viewPrefs.getMeasureColumns('automatic') ?? {
      status: false,
      calculated: false,
      verification: false,
    },
  );

  readonly measureName = computed(() => this.measure()?.name ?? '');

  // Single gate for edit and delete: the staged draft saves through the bulk endpoint.
  readonly canWrite = permissionGrantedSignal(
    computed(() => {
      const assetId = this.measure()?.assetId;
      return assetId ? { method: 'postAutomaticMeasureFollowUpValuesBulk' as const, assetId } : undefined;
    }),
  );

  readonly isSaving = signal(false);

  readonly hasDraft = computed(() => {
    const measureId = this.measure()?.id;
    return measureId != null && this.draftSvc.hasDraft(measureId);
  });

  /** Excel pastes on the editable column land as numbers (blank = erase), never raw strings. */
  readonly clipboardColumnType = (colId: string): ClipboardColumnType =>
    colId === 'inputValue' ? { kind: 'numeric' } : { kind: 'passthrough' };

  readonly measureDetailUrl = computed(() => {
    const measure = this.measure();
    if (!measure?.assetId || !measure?.id) return null;
    return `/assets/${measure.assetId}/configuration/measures/automatic/${measure.id}/info`;
  });

  readonly measureFrequency = computed(() => {
    this.translations();
    const freq = this.measure()?.frequency;
    if (freq == null) return '';
    const key = FREQUENCY_OPTIONS.find(o => o.id === freq)?.name;
    return key ? this.translate.translate(key) : '';
  });

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    const assetId = this.route.parent?.parent?.parent?.snapshot.paramMap.get('id') ?? undefined;
    return [
      {
        label: this.translate.translate('navigation.routes.automaticMeasurements'),
        routerLink: ['/assets', assetId, 'monitoring', 'automatic-measurements'],
      },
      { label: this.measureName() },
    ];
  });

  readonly columnDefs = computed<ColDef<AutomaticMeasureValue>[]>(() => {
    this.translations();
    const visible = this.visibleColumns();
    const unit = this.unitSymbol();
    const acquiredUnit = this.acquiredValueUnit();
    const canEditInput = this.canWrite() === true;
    const numericCol = (
      field: 'inputValue' | 'value' | 'valueMinChecker' | 'valueMaxChecker',
      labelKey: string,
      opts: { width?: number; minWidth?: number; unit?: string } = {},
    ): ColDef<AutomaticMeasureValue, number | null | undefined> => ({
      field,
      headerComponent: AutomaticMeasureHeaderComponent,
      headerComponentParams: { label: this.translate.translate(labelKey), unit: opts.unit ?? unit } as AutomaticMeasureHeaderParams,
      filter: 'agNumberColumnFilter',
      filterParams: ENTER_APPLY_NUMBER_FILTER_PARAMS,
      width: opts.width ?? 160,
      minWidth: opts.minWidth ?? 140,
      cellClass: 'text-right',
      // Live calculation pushes refresh rows in place; flash the cells whose value moved.
      enableCellChangeFlash: true,
      valueFormatter: (params: ValueFormatterParams<AutomaticMeasureValue, number | null | undefined>) =>
        formatNumericValue(params.value),
    });

    const inputValueCol: ColDef<AutomaticMeasureValue, number | null | undefined> = {
      ...numericCol('inputValue', 'common.labels.inputValue', { unit: acquiredUnit }),
      editable: canEditInput,
      cellEditor: NumericCellEditorComponent,
      // Same editable affordance as the campaign grid: pencil header + tinted cells, writers only.
      ...(canEditInput ? { headerClass: 'header-editable' } : {}),
      // SSRM block reloads re-deliver server values; the staged draft overlays at render time.
      valueGetter: (params: ValueGetterParams<AutomaticMeasureValue, number | null | undefined>) => {
        const staged = this.stagedEntry(params.data);
        return staged !== undefined ? staged.inputValue : (params.data?.inputValue ?? null);
      },
      cellClassRules: {
        'cell-editable-input': () => canEditInput,
        'cell-modified': params => this.stagedEntry(params.data) !== undefined,
      },
      tooltipValueGetter: (params: ITooltipParams<AutomaticMeasureValue>) => {
        const staged = this.stagedEntry(params.data);
        return staged === undefined ? null : this.originalValueTooltip(staged.originalInputValue);
      },
      suppressKeyboardEvent: params => (params.editing ? this.chainEditOnEnter(params) : this.clearRangeOnDeleteKey(params)),
      onCellValueChanged: params => this.onInputValueEdited(params),
    };

    const cols: ColDef<AutomaticMeasureValue>[] = [
      {
        field: 'timestamp',
        pinned: 'left',
        headerComponent: AutomaticMeasureHeaderComponent,
        headerComponentParams: { label: this.translate.translate('common.labels.date') } as AutomaticMeasureHeaderParams,
        sort: 'desc',
        width: 180,
        minWidth: 180,
        cellClass: 'text-left',
        filter: CesDateRangeGridFilterComponent,
        valueFormatter: (params: ValueFormatterParams<AutomaticMeasureValue, Date>) => formatGridDateTime(params.value),
        suppressKeyboardEvent: params => this.clearRangeOnDeleteKey(params),
      },
      inputValueCol,
    ];

    if (visible.status) {
      cols.push({
        field: 'status',
        headerComponent: AutomaticMeasureHeaderComponent,
        headerComponentParams: { label: this.translate.translate('common.labels.status') } as AutomaticMeasureHeaderParams,
        cellRenderer: MeasureStatusBadgeComponent,
        cellClass: 'text-left',
        width: 150,
        minWidth: 130,
        // Column-level formatter drives clipboard/export; the set filter ignores it when
        // `values` are provided, so the same formatter is repeated in filterParams.
        valueFormatter: (params: { value: MeasureValueStatus | null | undefined }) =>
          params.value == null ? '' : this.statusLabel(params.value),
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            MeasureValueStatus.Empty,
            MeasureValueStatus.Error,
            MeasureValueStatus.OutOfBounds,
            MeasureValueStatus.Unchecked,
            MeasureValueStatus.Valid,
            MeasureValueStatus.Pending,
          ],
          valueFormatter: (params: { value: MeasureValueStatus | null }) =>
            params.value == null ? '' : this.statusLabel(params.value),
          ...EXCEL_SET_FILTER_PARAMS,
        },
      });
    }

    if (visible.calculated) {
      cols.push(numericCol('value', 'tables.data.valueTypes.calculated'));
    }

    if (visible.verification) {
      cols.push(
        numericCol('valueMinChecker', 'tables.data.valueTypes.verificationMin', { width: 170, minWidth: 150 }),
        numericCol('valueMaxChecker', 'tables.data.valueTypes.verificationMax', { width: 170, minWidth: 150 }),
      );
    }

    return cols;
  });

  private requestVersion = 0;
  private dateInitialized = false;
  // Guards the load-failure toast so the perpetual poll can't spam it every tick.
  private loadErrorNotified = false;

  readonly datasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => this.fetchPage(params),
  };

  ngOnInit(): void {
    const { startDate, endDate } = this.viewPrefs.getDateRange();
    if (startDate && endDate) {
      this.dateNav.setDates(startDate, endDate);
    }

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const measureId = Number(params['id']);
      if (!Number.isFinite(measureId) || measureId <= 0) {
        void this.router.navigate(['/404']);
        return;
      }
      // Fresh measure, fresh error state: don't let a prior measure's failed streak
      // suppress this one's first load-failure toast.
      this.loadErrorNotified = false;
      void this.loadMeasureThenRefresh(measureId);
    });
  }

  private onRemoteMeasureUpdate(): void {
    if (this.timeSeries()?.isEditing()) return;
    // A refetch mid-draft would churn the copy buffer and fight the overlay.
    if (this.hasDraft()) return;
    this.refreshGridData();
  }

  private currentMeasureId(): number | null {
    const measureId = Number(this.route.snapshot.params['id']);
    return Number.isFinite(measureId) && measureId > 0 ? measureId : null;
  }

  onColumnVisibilityChange(visibility: MeasureColumnVisibility): void {
    this.visibleColumns.set(visibility);
    this.viewPrefs.saveMeasureColumns('automatic', visibility);
  }

  onWindowChanged(): void {
    this.resetRowBuffer();
    this.timeSeries()?.deselectAll();
    if (this.dateInitialized) {
      this.viewPrefs.saveDateRange(this.dateNav.startDate(), this.dateNav.endDate());
    }
  }

  // Invalidating the version discards in-flight pages so the refetched blocks rebuild the copy buffer exactly once.
  private resetRowBuffer(): void {
    this.requestVersion++;
    this.loadedRows.set([]);
    this.totalRowCount.set(0);
    this.lastFetchSettled.set(false);
  }

  private refreshGridData(purge = false): void {
    this.resetRowBuffer();
    this.timeSeries()?.refreshData(purge);
  }

  onUserRefresh(): void {
    this.hasRefreshed.set(true);
    this.onWindowChanged();
  }

  // Arms the grid on a linked window, skipping onWindowChanged's date save: the range is the
  // link's, not a choice, so the date preference shared with the other screens stays as it was.
  onUrlWindow(): void {
    this.hasRefreshed.set(true);
  }

  onCellsCopied(count: number): void {
    this.toast.info('common.toasts.cellsCopied', undefined, undefined, undefined, { count });
  }

  onInputValueEdited(params: NewValueParams<AutomaticMeasureValue, number | null | undefined>): void {
    const row = params.data;
    const measureId = this.currentMeasureId();
    if (!row || measureId == null) return;
    const staged = this.stagedEntry(row);
    // Compare against the first-seen server value: oldValue is the drafted value on re-edits.
    const original = staged ? staged.originalInputValue : (params.oldValue ?? null);
    const entry: StagedValueUpdate = {
      inputValue: params.newValue ?? null,
      originalInputValue: original,
      timestamp: row.timestamp,
    };
    if (row.id != null) {
      this.draftSvc.stageUpdate(measureId, row.id, entry);
    } else {
      this.draftSvc.stageCreate(measureId, row.timestamp.toISOString(), entry);
    }
    // suppressFlash: the green change-flash is reserved for recalculated values
    // pushed over the websocket; staging feedback is the orange modified marker.
    if (params.node) params.api.refreshCells({ rowNodes: [params.node], force: true, suppressFlash: true });
  }

  // Delete-key (cell range) deletion: one confirmation popup, then the deletion runs
  // immediately (no separate save step) and the grid refreshes. The popup names the
  // date spans, never a row count: the virtualised grid cannot count rows whose blocks
  // were never loaded, so the only honest figure is the one the server reports back.
  private async confirmRangeDeletion(measureId: number, spans: DateSpan[]): Promise<void> {
    if (this.canWrite() !== true) return;
    const ranges = spans
      .map(span =>
        this.translate.translate('entities.automaticMeasure.followUp.dateSpan', {
          from: formatGridDateTime(span.from),
          to: formatGridDateTime(span.to),
        }),
      )
      .join(', ');
    await this.confirmationHelper.confirmAndDelete({
      entityName: '',
      messageKey: 'entities.automaticMeasure.followUp.confirmDeleteRange',
      params: { ranges },
      // Le voile nomme la plage traitée, pas seulement « patientez ». Une
      // sélection s'étire sur des années sans que la grille virtualisée les
      // montre : l'étendue réelle n'est lisible nulle part ailleurs.
      busyKey: 'entities.automaticMeasure.followUp.deletingRange',
      busyParams: { ranges },
      onDelete: async () => {
        // One call per span: disjoint selections must not take the gap between them along.
        let deleted = 0;
        const cleared: DateSpan[] = [];
        try {
          for (const span of spans) {
            deleted += (await this.valueStore.clearRange({ measureId, dateFrom: span.from, dateTo: span.to })).deleted;
            cleared.push(span);
          }
        } finally {
          // The spans are separate requests, so a failure on one leaves the earlier ones
          // deleted. Settle on what the server actually dropped: a grid still listing
          // removed rows, or a draft still naming them, is worse than the error itself.
          this.draftSvc.dropWithin(measureId, cleared);
          this.timeSeries()?.deselectAll();
          this.refreshGridData(true);
          if (deleted > 0) {
            this.toast.success('common.toasts.valuesDeleted', undefined, undefined, undefined, { count: deleted });
          }
        }
      },
    });
  }

  saveDraft(): void {
    const measureId = this.currentMeasureId();
    if (measureId == null) return;
    void this.commitDraft(measureId);
  }

  cancelDraft(): void {
    const measureId = this.currentMeasureId();
    if (measureId == null) return;
    this.draftSvc.clear(measureId);
    this.timeSeries()?.redrawRows();
    // Edits were written into the SSRM row data optimistically; refetch restores server values.
    this.refreshGridData();
    this.toast.info('common.toasts.changesCancelled');
  }

  private async commitDraft(measureId: number): Promise<void> {
    const draft = this.draftSvc.draftFor(measureId);
    const total = draft.updates.size + draft.creates.size;
    if (total === 0) return;
    this.isSaving.set(true);
    try {
      await this.valueStore.saveBatch({
        measureId,
        updates: [...draft.updates.entries()].map(([id, staged]) => ({ id, inputValue: staged.inputValue })),
        creates: [...draft.creates.values()].map(staged => ({ timestamp: staged.timestamp, inputValue: staged.inputValue })),
      });
      this.draftSvc.clear(measureId);
      this.toast.success('common.toasts.itemsSaved', undefined, undefined, undefined, { count: total });
      this.refreshGridData();
    } catch {
      // The draft is kept so the user can retry without losing work.
      this.toast.error('common.toasts.saveFailed');
    } finally {
      this.isSaving.set(false);
    }
  }

  private stagedEntry(row: AutomaticMeasureValue | undefined): StagedValueUpdate | undefined {
    const measureId = this.currentMeasureId();
    if (measureId == null || !row) return undefined;
    return row.id != null
      ? this.draftSvc.stagedUpdate(measureId, row.id)
      : this.draftSvc.stagedCreate(measureId, row.timestamp.toISOString());
  }

  // Excel-style fast entry: Enter commits the edit and opens the editor on the
  // next row (Shift+Enter goes up), skipping rows the grid has not loaded.
  private chainEditOnEnter(params: SuppressKeyboardEventParams<AutomaticMeasureValue>): boolean {
    if (params.event.key !== 'Enter') return false;
    params.event.preventDefault();
    params.api.stopEditing();
    const direction = params.event.shiftKey ? -1 : 1;
    const rowCount = params.api.getDisplayedRowCount();
    let nextIndex = (params.node.rowIndex ?? -1) + direction;
    while (nextIndex >= 0 && nextIndex < rowCount) {
      if (params.api.getDisplayedRowAtIndex(nextIndex)?.data) {
        params.api.startEditingCell({ rowIndex: nextIndex, colKey: 'inputValue' });
        break;
      }
      nextIndex += direction;
    }
    return true;
  }

  private clearRangeOnDeleteKey(params: SuppressKeyboardEventParams<AutomaticMeasureValue>): boolean {
    if (params.editing) return false;
    if (params.event.key !== 'Delete' && params.event.key !== 'Backspace') return false;
    if (this.canWrite() !== true) return false;
    const measureId = this.currentMeasureId();
    const valueRanges = (params.api.getCellRanges() ?? []).filter(range =>
      range.columns?.some(column => column.getColId() === 'inputValue'),
    );
    // Applied window, not the picker dates: it is the span the grid actually shows, and its
    // end bound is the end of the last day, so a whole-column delete covers that day too.
    const windowFrom = this.dateNav.appliedStartDate();
    const windowTo = this.dateNav.appliedEndDate();
    if (measureId == null || !valueRanges.length || !windowFrom || !windowTo) return false;
    // Suppressed from here on: AG Grid's default Backspace starts editing with cleared content.
    params.event.preventDefault();
    // Each selection resolves to the date interval between its first and last row rather than
    // to the rows themselves: the virtualised grid (SSRM, <=1000 rows cached, fast scroll skips
    // blocks) can't enumerate a large selection, which previously left the unloaded middle
    // untouched. That interval only equals the selection when rows are in date order with
    // nothing hidden: sorted by value the edges carry arbitrary dates, and a filter hides rows
    // the interval would still delete.
    const query = this.lastQuery();
    if (query?.filter || (query?.sortField && query.sortField !== 'timestamp')) {
      this.toast.warning('entities.automaticMeasure.followUp.deleteNeedsDateOrder');
      return true;
    }
    // In date order the top row is the later date when sorted descending.
    const [topLimit, bottomLimit] = (query?.sortOrder ?? 'desc') === 'desc' ? [windowTo, windowFrom] : [windowFrom, windowTo];
    const spans = valueRanges.map((range): DateSpan => {
      const startIndex = Math.min(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
      const endIndex = Math.max(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
      // An edge whose block is not cached (whole column / fast scroll) falls back to the window
      // limit on its own side; the other edge keeps its date.
      const top = params.api.getDisplayedRowAtIndex(startIndex)?.data?.timestamp ?? topLimit;
      const bottom = params.api.getDisplayedRowAtIndex(endIndex)?.data?.timestamp ?? bottomLimit;
      return {
        from: new Date(Math.min(top.getTime(), bottom.getTime())),
        to: new Date(Math.max(top.getTime(), bottom.getTime())),
      };
    });
    void this.confirmRangeDeletion(measureId, spans);
    return true;
  }

  async copyValues(): Promise<void> {
    const measureId = this.currentMeasureId();
    const dateFrom = this.dateNav.appliedStartDate();
    const dateTo = this.dateNav.appliedEndDate();
    const total = this.totalRowCount();
    if (measureId == null || !dateFrom || !dateTo || total === 0) {
      this.toast.warning('common.toasts.noValuesToCopy');
      return;
    }
    let count = 0;
    const text = (async () => {
      const rows = await this.collectAllRows(measureId, dateFrom, dateTo, total);
      count = rows.length;
      return this.buildClipboardTsv(rows);
    })();
    try {
      await writeClipboardText(text);
      this.toast.success('common.toasts.valuesCopied', undefined, undefined, undefined, { count });
    } catch {
      this.toast.error('common.toasts.copyFailed');
    }
  }

  // Builds the TSV (header + one line per row), overlaying staged edits (incl. staged
  // nulls) so the clipboard matches the screen. Columns follow the visible set.
  private buildClipboardTsv(rows: AutomaticMeasureValue[]): string {
    const visible = this.visibleColumns();
    const unit = this.unitSymbol();
    const headers = [
      this.translate.translate('common.labels.date'),
      this.headerLabel('common.labels.inputValue', this.acquiredValueUnit()),
    ];
    if (visible.status) headers.push(this.translate.translate('common.labels.status'));
    if (visible.calculated) headers.push(this.headerLabel('tables.data.valueTypes.calculated', unit));
    if (visible.verification) {
      headers.push(this.headerLabel('tables.data.valueTypes.verificationMin', unit));
      headers.push(this.headerLabel('tables.data.valueTypes.verificationMax', unit));
    }
    const lines = [headers.join('\t')];
    for (const row of rows) {
      const staged = this.stagedEntry(row);
      const inputValue = staged !== undefined ? staged.inputValue : row.inputValue;
      const cells = [formatGridDateTime(row.timestamp), formatClipboardNumericValue(inputValue)];
      if (visible.status) cells.push(this.statusLabel(row.status));
      if (visible.calculated) cells.push(formatClipboardNumericValue(row.value));
      if (visible.verification) {
        cells.push(formatClipboardNumericValue(row.valueMinChecker));
        cells.push(formatClipboardNumericValue(row.valueMaxChecker));
      }
      lines.push(cells.join('\t'));
    }
    return lines.join('\n');
  }

  // Pages the whole [dateFrom, dateTo] window server-side (the SSRM buffer only holds
  // scrolled blocks). Replays the grid's last sort/filter so the copy matches the view.
  private async collectAllRows(measureId: number, dateFrom: Date, dateTo: Date, total: number): Promise<AutomaticMeasureValue[]> {
    const query = this.lastQuery();
    const all: AutomaticMeasureValue[] = [];
    for (let startRow = 0; startRow < total; startRow += COPY_ALL_PAGE_SIZE) {
      const page = await this.valueStore.loadPage({
        measureId,
        dateFrom,
        dateTo,
        startRow,
        endRow: startRow + COPY_ALL_PAGE_SIZE,
        sortOrder: query?.sortOrder ?? 'desc',
        includeExpected: true,
        ...(query?.sortField ? { sortField: query.sortField } : {}),
        ...(query?.filter ? { filter: query.filter } : {}),
      });
      for (const v of page.rows) all.push(this.toRow(v));
      if (page.rows.length === 0) break;
    }
    return all;
  }

  private toRow(v: AutomaticValuesPage['rows'][number]): AutomaticMeasureValue {
    return {
      id: v.id ?? null,
      timestamp: new Date(v.timestamp),
      inputValue: v.inputValue ?? null,
      value: v.value ?? null,
      valueMinChecker: v.valueMinChecker ?? null,
      valueMaxChecker: v.valueMaxChecker ?? null,
      status: v.status,
    };
  }

  private headerLabel(key: string, unit: string): string {
    const label = this.translate.translate(key);
    return unit ? `${label} [${unit}]` : label;
  }

  private statusLabel(status: MeasureValueStatus): string {
    return this.translate.translate(STATUS_LABEL_KEYS[status]);
  }

  private originalValueTooltip(original: number | null): string {
    return this.translate.translate('entities.automaticMeasure.followUp.originalValue', { value: formatNumericValue(original) });
  }

  private fetchPage(params: IServerSideGetRowsParams): void {
    const measureId = this.currentMeasureId();
    // Applied window only: grid-initiated refetches (sort, filter, scroll) must not
    // adopt picker dates that were never confirmed through the refresh button.
    const dateFrom = this.dateNav.appliedStartDate();
    const dateTo = this.dateNav.appliedEndDate();
    if (measureId == null || !dateFrom || !dateTo) {
      params.fail();
      return;
    }
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? startRow + 100;
    const sortModel = params.request.sortModel;
    const sortField = sortModel?.[0]?.colId;
    const sortOrder: 'asc' | 'desc' = sortModel?.[0]?.sort === 'asc' ? 'asc' : 'desc';
    const filter = extractAutomaticMeasureFilter(params.request.filterModel);
    this.lastQuery.set({ sortField, sortOrder, filter });

    const version = this.requestVersion;
    this.rowsLoading.set(true);
    this.valueStore
      .loadPage({
        measureId,
        dateFrom,
        dateTo,
        startRow,
        endRow,
        sortOrder,
        includeExpected: true,
        ...(sortField ? { sortField } : {}),
        ...(filter ? { filter } : {}),
      })
      .then((page: AutomaticValuesPage) => {
        const rows = page.rows.map(v => this.toRow(v));
        const stale = version !== this.requestVersion;
        if (!stale) {
          this.totalRowCount.set(page.totalCount);
          // Sort/filter reloads restart at block 0 without a new version; rebuild the
          // copy buffer there instead of appending the re-delivered rows.
          this.loadedRows.update(prev => (startRow === 0 ? rows : [...prev, ...rows]));
          this.computingPoll.notify(page.computing ?? false);
          this.loadErrorNotified = false;
        }
        // Failing a settled page renders AG Grid's raw "ERR" row; deliver it, then
        // replay the superseding refresh that was swallowed while this block was loading.
        params.success({ rowData: rows, rowCount: page.totalCount });
        if (stale) this.timeSeries()?.refreshData();
      })
      .catch(() => {
        // The self-perpetuating computing poll retries silently every interval; toast a
        // durable failure once per streak instead of on every tick. Reset on success.
        if (version === this.requestVersion && !this.loadErrorNotified) {
          this.loadErrorNotified = true;
          this.toast.error('common.toasts.loadFailed');
        }
        params.fail();
      })
      .finally(() => {
        if (version === this.requestVersion) {
          this.rowsLoading.set(false);
          this.lastFetchSettled.set(true);
        }
      });
  }

  private async loadMeasureThenRefresh(measureId: number): Promise<void> {
    const ok = await this.loadMeasure(measureId);
    if (ok) {
      this.onWindowChanged();
      this.dateInitialized = true;
      this.wsRefresh.startFallbackPoll();
    }
  }

  // Guards route re-entry: a superseded load resolving late must not overwrite
  // the signals of the measure currently displayed.
  private loadMeasureVersion = 0;

  private async loadMeasure(measureId: number): Promise<boolean> {
    const version = ++this.loadMeasureVersion;
    this.isLoading.set(true);
    this.loadError.set(false);
    this.acquisitionUnitSymbol.set('');
    try {
      const measure = await this.automaticStore.loadById(measureId);
      const unitSymbol = await this.fetchUnitSymbol(measure);
      if (version !== this.loadMeasureVersion) return false;
      this.unitSymbol.set(unitSymbol);
      this.measure.set(measure);
      // Header decoration only: resolved in the background so a slow lookup never
      // gates the grid; the columnDefs computed repaints the headers when it lands.
      void this.resolveAcquisitionUnitSymbol(measure, version);
      return true;
    } catch {
      if (version === this.loadMeasureVersion) {
        this.toast.error('common.toasts.loadFailed');
        this.loadError.set(true);
      }
      return false;
    } finally {
      if (version === this.loadMeasureVersion) this.isLoading.set(false);
    }
  }

  private async fetchUnitSymbol(measure: AutomaticMeasure | null): Promise<string> {
    const unitId = measure?.unitId;
    if (!unitId) return '';
    try {
      const unit = await this.unitStore.loadById(unitId);
      return unit?.symbol ?? '';
    } catch {
      return '';
    }
  }

  // The active formula's single input IS the SCADA acquisition; its unit documents
  // the acquired value. Empty (no formula, suspended, lookup failure) falls back to
  // the measure unit at render time.
  private async resolveAcquisitionUnitSymbol(measure: AutomaticMeasure, version: number): Promise<void> {
    if (!measure.hasFormula) return;
    try {
      const inputs = await firstValueFrom(
        this.formulaInputsApi.postFormulaInputLookup<FormulaInputSummary[]>({ measureIds: [measure.id] }),
      );
      if (version === this.loadMeasureVersion) this.acquisitionUnitSymbol.set(inputs[0]?.unitSymbol ?? '');
    } catch {
      // Decoration only: the header keeps the measure-unit fallback.
    }
  }
}
